import React, { useReducer } from 'react'
import './App.css'
import Ball from './components/Ball'
import Card from './components/Card'
import Alert from './components/Alert'
import {
  PATTERN_X,
  PATTERN_PLUS,
  PATTERN_MINUS,
  PATTERN_L,
  PATTERN_C,
  PATTERN_E,
  PATTERN_M,
  PATTERN_BOX,
  PATTERN_U,
  PATTERN_T,
  PATTERN_UMBRELLA,
  PATTERN_DIAMOND,
  PATTERN_DIAMOND_FULL,
} from './components/Patterns'
import CompleteNumbersBoard from './components/CompleteNumbersBoard'
import FrontAnimation from './components/FrontAnimation'
import HeaderTitle from './components/HeaderTitle'
import styled from 'styled-components'
import { AnimatePresence, motion } from 'framer-motion'

const Header = styled.header`
  max-width: 1400px;
  margin: 0 auto;
  padding: 10px;
  display: flex;
  justify-content: space-between;
  overflow: hidden;

  @media (max-width: 1000px) {
    display: block;
    text-align: center;
  }
`

const Body = styled.div`
  max-width: 1400px;
  width: 100%;
  margin: 0 auto 0;
  padding: 20px;
  display: grid;
  grid-template-columns: auto auto auto;
  grid-gap: 20px;
  justify-content: center;
  overflow: hidden;

  &.autoplay {
    pointer-events: none;
  }

  &.padded {
    padding-bottom: 280px;
  }

  @media (max-width: 1400px) {
    grid-template-columns: auto auto;
  }

  @media (max-width: 940px) {
    grid-template-columns: 440px;
  }

  @media (max-width: 460px) {
    grid-template-columns: 100%;
  }

  @media print {
    margin: 0;
    padding: 0;
    display: flex;
    flex-wrap: wrap;
    justify-content: flex-start;
  }
`

const CardNumberStart = styled(motion.div)`
  padding: 10px 0 10px 10px;
  margin: 0 auto;
  text-align: center;
  display: flex;
  justify-content: center;
  font-size: 20px;
  align-items: start;

  @media (max-width: 460px) {
    justify-content: center;
  }

  @media print {
    display: none;
  }

  > label {
    display: block;
    font-size: 16px;

    @media (max-width: 460px) {
      font-size: 16px;
    }

    > input {
      width: 80px;
      height: 32px;
      margin: 0 10px;
      padding: 3px 5px;
      font-size: 20px;
      text-align: center;

      @media (max-width: 460px) {
        font-size: 14px;
      }

      &::placeholder {
        font-size: 14px;
        color: #999;
      }
    }
  }

  button {
    padding: 5px 10px;
    font-size: 13px;
  }
`

const GenericButton = styled.div`
  margin: 0 0 10px;
  display: grid;
  grid-template-columns: 180px 180px;
  grid-gap: 5px;
  justify-content: center;

  @media (max-width: 380px) {
    display: block;
  }

  @media print {
    display: none;
  }

  > button {
    padding: 6px 16px;

    @media (max-width: 380px) {
      width: 100%;
      margin-bottom: 5px;
      display: block;
    }
  }
`

const Selections = styled.div`
  margin-bottom: 10px;
  display: flex;
  justify-content: center;

  @media print {
    display: none;
  }

  @media (max-width: 360px) {
    display: block;
  }
`

const ThemeSelection = styled.label`
  margin: 0 5px 5px;
  display: block;
  font-size: 13px;
`
const initialState = {
  newCard: [],
  cardNumber: 1,
  playing: false,
  guide: true,
  alert: false,
  isOpenBallArea: false,
  isBallSet: false,
  theme: '',
  isAutoPlaying: false,
  pattern: false,
}

function reducer(state, action) {
  switch (action.type) {
    case 'NEW_GAME':
      return {
        ...initialState,
        theme: state.theme,
      }

    case 'NEW_CARD':
      return {
        ...state,
        newCard: action.payload,
      }

    case 'CHANGE_CARD_NUMBER':
      return {
        ...state,
        cardNumber: action.payload,
      }

    case 'TOGGLE_PLAYING':
      return {
        ...state,
        playing: !state.playing,
      }

    case 'TOGGLE_GUIDE':
      return {
        ...state,
        guide: !state.guide,
      }

    case 'TOGGLE_BALL_AREA':
      return {
        ...state,
        isOpenBallArea: !state.isOpenBallArea,
      }

    case 'TOGGLE_ALERT':
      return {
        ...state,
        alert: !state.alert,
      }

    case 'BALL_SET':
      return {
        ...state,
        isBallSet: true,
      }

    case 'BALL_UNSET':
      return {
        ...state,
        isBallSet: false,
      }

    case 'SET_AUTOPLAY':
      return {
        ...state,
        isAutoPlaying: action.payload,
      }

    case 'CHANGE_THEME':
      return {
        ...state,
        theme: action.payload,
      }

    case 'CHANGE_PATTERN':
      return {
        ...state,
        pattern: action.payload,
      }

    default:
      return {
        ...state,
      }
  }
}

function App() {
  const [state, dispatch] = useReducer(reducer, initialState)

  const handleCardChange = (e) => {
    if (e.target.value < 1 || !e.target.value || isNaN(e.target.value)) {
      dispatch({ type: 'CHANGE_CARD_NUMBER', payload: 0 })
      return
    }
    dispatch({ type: 'CHANGE_CARD_NUMBER', payload: e.target.value })
    dispatch({ type: 'NEW_CARD', payload: [] })
  }

  const handleCardChangeKeyPress = (e) => {
    if (state.cardNumber < 1 && state.newCard.length === 0) return
    if (e.key === 'Enter') {
      handleGenerateCard()
    }
  }

  const handleGenerateCard = () => {
    let cards = []
    dispatch({ type: 'TOGGLE_PLAYING' })
    for (let x = 0; x < state.cardNumber; x++) {
      cards.push(x + 1)
    }
    resetCheckbox()
    dispatch({ type: 'NEW_CARD', payload: cards })
  }

  const resetCheckbox = () => {
    document
      .querySelectorAll('input[type=checkbox]')
      .forEach((i) => (i.checked = false))
  }

  const handlePatternChange = (e) => {
    let patternStr = ''
    // Reset
    document
      .querySelectorAll('label.target')
      .forEach((el) => el.classList.remove('target'))

    dispatch({ type: 'CHANGE_PATTERN', payload: true })
    switch (e.target.value) {
      case 'x':
        patternStr = PATTERN_X
        break

      case 'plus':
        patternStr = PATTERN_PLUS
        break

      case 'minus':
        patternStr = PATTERN_MINUS
        break

      case 'l':
        patternStr = PATTERN_L
        break

      case 'c':
        patternStr = PATTERN_C
        break

      case 'e':
        patternStr = PATTERN_E
        break

      case 'm':
        patternStr = PATTERN_M
        break

      case 'box':
        patternStr = PATTERN_BOX
        break

      case 'u':
        patternStr = PATTERN_U
        break

      case 't':
        patternStr = PATTERN_T
        break

      case 'diamond':
        patternStr = PATTERN_DIAMOND
        break

      case 'diamond-full':
        patternStr = PATTERN_DIAMOND_FULL
        break

      case 'umbrella':
        patternStr = PATTERN_UMBRELLA
        break

      default:
        dispatch({ type: 'CHANGE_PATTERN', payload: false })
        document
          .querySelectorAll('.card')
          .forEach((el) => el.classList.remove('pattern'))
    }

    if (patternStr) {
      // Add parent class pattern
      document
        .querySelectorAll('.card')
        .forEach((el) => el.classList.add('pattern'))
      // Add .target to each number
      document
        .querySelectorAll(patternStr)
        .forEach((el) => el.classList.add('target'))
    }
  }

  return (
    <div className={`App ${state.theme}`}>
      <Header>
        <div className="left">
          <HeaderTitle />
          {state.playing ? (
            <>
              <GenericButton>
                <button onClick={() => dispatch({ type: 'TOGGLE_ALERT' })}>
                  New Card{state.cardNumber > 1 ? 's' : ''}
                </button>
                <button onClick={() => dispatch({ type: 'TOGGLE_GUIDE' })}>
                  {state.guide ? 'Hide' : 'Show'} Number Guide
                </button>
                <button onClick={() => window.print()}>Print Card</button>
                <button
                  onClick={() =>
                    dispatch({
                      type: 'TOGGLE_BALL_AREA',
                    })
                  }
                  disabled={state.isBallSet}
                >
                  {state.isOpenBallArea ? 'Hide' : 'Show'} Ball Panel
                </button>
              </GenericButton>
              <Selections>
                <ThemeSelection htmlFor="">
                  Select Theme{' '}
                  <select
                    onChange={(e) =>
                      dispatch({
                        type: 'CHANGE_THEME',
                        payload: e.target.value,
                      })
                    }
                    defaultValue={state.theme}
                  >
                    <option value="">Beverly</option>
                    <option value="mark">Mark</option>
                    <option value="lerma">Lerma</option>
                    <option value="tony">Tony</option>
                    <option value="sunset">Sunset</option>
                    <option value="black">Black</option>
                  </select>
                </ThemeSelection>
                <ThemeSelection htmlFor="">
                  Select Pattern{' '}
                  <select onChange={handlePatternChange}>
                    <option value="">None</option>
                    <option value="c">C</option>
                    <option value="e">E</option>
                    <option value="l">L</option>
                    <option value="m">M</option>
                    <option value="t">T</option>
                    <option value="u">U</option>
                    <option value="x">X</option>
                    <option value="box">Box</option>
                    <option value="diamond">Diamond</option>
                    <option value="diamond-full">Diamond Full</option>
                    <option value="minus">Minus (-)</option>
                    <option value="plus">Plus (+)</option>
                    <option value="umbrella">Umbrella</option>
                  </select>
                </ThemeSelection>
              </Selections>
            </>
          ) : (
            ''
          )}
        </div>
        <div className="right">
          <AnimatePresence>
            {state.playing ? (
              <CompleteNumbersBoard
                visibility={state.guide ? '' : 'hide'}
                isAutoPlaying={state.isAutoPlaying}
              />
            ) : (
              <CardNumberStart
                initial={{ opacity: 0, x: -100 }}
                animate={{ opacity: 1, x: 0 }}
              >
                <label htmlFor="">
                  No. of Cards:
                  <input
                    type="tel"
                    onChange={handleCardChange}
                    onKeyPress={handleCardChangeKeyPress}
                    maxLength="2"
                    placeholder="1-99 Only"
                    pattern="[0-9]"
                    autoFocus
                    defaultValue={state.cardNumber}
                  />
                </label>
                <button
                  onClick={handleGenerateCard}
                  disabled={state.cardNumber === 0}
                >
                  Play
                </button>
              </CardNumberStart>
            )}
          </AnimatePresence>
        </div>
      </Header>
      <Body
        className={`${state.isOpenBallArea ? 'padded' : ''} ${
          state.isAutoPlaying ? 'autoplay' : ''
        }`}
      >
        {state.newCard.length
          ? state.newCard.map((x) => (
              <Card
                name={`card-${x}`}
                key={x}
                number={x}
                pattern={state.pattern}
              />
            ))
          : ''}
      </Body>
      {state.newCard.length ? '' : <FrontAnimation />}
      <AnimatePresence>
        {state.alert && (
          <Alert
            cardNumber={state.cardNumber}
            newGame={() => dispatch({ type: 'NEW_GAME' })}
            setAlert={() => dispatch({ type: 'TOGGLE_ALERT' })}
          />
        )}
      </AnimatePresence>
      <AnimatePresence>
        {state.isOpenBallArea && (
          <Ball
            setIsBallSet={() => dispatch({ type: 'BALL_SET' })}
            unsetIsBallSet={() => dispatch({ type: 'BALL_UNSET' })}
            isBallSet={state.isBallSet}
            isAutoPlaying={state.isAutoPlaying}
            setIsAutoPlayingDispatch={dispatch}
            resetCheckbox={resetCheckbox}
          />
        )}
      </AnimatePresence>
    </div>
  )
}

export default App
